import { Injectable, Signal, computed, inject } from '@angular/core';
import { Store } from '@ngrx/store';

import { KNOWN_FEATURE_FLAGS } from '@core/models/feature-flags/known-feature-flags';
import { FeatureFlagsFacade } from '@core/services/feature-flags/feature-flags.facade';
import { selectFeatureFlagsState } from '@core/store/feature-flags/feature-flags.selectors';
import { UserPreferencesActions } from '@core/store/user-preferences/user-preferences.actions';
import { FilterGrammar } from '@core/store/user-preferences/user-preferences.model';
import {
  selectFilterGrammar,
  selectUserPreferencesSettled
} from '@core/store/user-preferences/user-preferences.selectors';

export type { FilterGrammar };

@Injectable({ providedIn: 'root' })
export class FilterGrammarService {
  private readonly store = inject(Store);
  private readonly saved = this.store.selectSignal(selectFilterGrammar);
  private readonly flags = this.store.selectSignal(selectFeatureFlagsState);
  private readonly preferencesSettled = this.store.selectSignal(
    selectUserPreferencesSettled
  );

  /**
   * `available` and `grammar` are final: the flags arrived, or their load failed, so nothing waits
   * forever on them. A reload (data space switch) keeps the previous space's flags until it ends, so
   * it is not settled. With the flag on, the saved grammar matters too: until the preferences load it
   * reads as Classic.
   */
  readonly settled: Signal<boolean> = computed(() => {
    const { loaded, loading, error } = this.flags();
    if (loading || !(loaded || error !== null)) return false;
    return !this.available() || this.preferencesSettled();
  });

  /** Whether filter bars may offer Path at all in the current data space. */
  readonly available: Signal<boolean> = inject(FeatureFlagsFacade).isActive(
    KNOWN_FEATURE_FLAGS.ENABLE_PATH_FILTERS
  );

  /** A saved Path choice is ignored while the flag is off, so turning it off restores Classic everywhere. */
  readonly grammar: Signal<FilterGrammar> = computed(() =>
    this.available() ? this.saved() : 'classic'
  );

  private readonly beforeSwitch = new Set<() => void>();

  /**
   * Runs `listener` just before any bar saves a new grammar. An open bar uses it to keep the
   * grammar it shows: following the switch would hide its filters without the clean-up its own
   * switch runs. Returns the unregister function.
   */
  onBeforeSwitch(listener: () => void): () => void {
    this.beforeSwitch.add(listener);
    return () => this.beforeSwitch.delete(listener);
  }

  setGrammar(grammar: FilterGrammar): void {
    this.beforeSwitch.forEach((listener) => listener());
    this.store.dispatch(UserPreferencesActions.setFilterGrammar({ grammar }));
  }
}
